import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {USPsSection_ArticleUSP_DeliveryEstimation} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {useTrackFeatureInViewImpression} from '@aboutyou/tracking';
import {
  COLORS,
  ContentPopper,
  FONT_SIZES,
  GenericIconName,
  Icon,
  Image,
  SecondaryContentPopperButton,
  Text,
  UnstyledButton,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {USPsViewProps} from './USPsView';
import {Badge, UspItem} from './UspItem';

export const UspDeliveryTime: React.FC<{
  deliverySection: USPsSection_ArticleUSP_DeliveryEstimation;
  InfoLayoutComponent: USPsViewProps['InfoLayoutComponent'];
}> = ({deliverySection, InfoLayoutComponent}) => {
  const {t} = useTranslation();
  const {isOpen, open, cancel} = useOpenedStateManager('USP_INFO');
  const {isActive: isDeliveryEstimationActive} = useIsFeatureActive(
    'adp_delivery_estimation',
  );

  const deliveryTimeHint = deliverySection.deliveryTimeHint;

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    ...deliverySection.featureTracker,
  });

  if (!isDeliveryEstimationActive) {
    return null;
  }

  return (
    <>
      <UspItem
        data-testid="uspDeliveryTime"
        ref={ref}
        image={deliverySection.image}
        text={deliverySection.label}
        badge={
          deliveryTimeHint && (
            <>
              <Badge
                data-testid="uspDeliveryTimeBadge"
                $backgroundColor={deliveryTimeHint.color.hex}
              >
                {deliveryTimeHint.vipImage && (
                  <BadgeIcon
                    data-testid="uspDeliveryTimeBadgeVipIcon"
                    src={deliveryTimeHint.vipImage.url}
                    alt={deliveryTimeHint.vipImage.description}
                    ratio="1:1"
                    widths={[16, 32]}
                    breakpoints={[]}
                  />
                )}
                {deliveryTimeHint.label}
              </Badge>
            </>
          )
        }
      >
        <InfoSection>
          {process.env.STACK === 'mobile'
            ? t('Final expected delivery times will be shown in your basket.')
            : deliverySection.description}

          <InfoButton
            onClick={() => open()}
            aria-label={t('open usp delivery time info window')}
          >
            <Icon asset={GenericIconName.Info} size="XSS" />
          </InfoButton>
        </InfoSection>
      </UspItem>

      <ContentPopper
        open={isOpen}
        renderInPortal
        LayoutComponent={InfoLayoutComponent!}
        onClose={cancel}
        headerContent={
          <InfoHeaderText>{deliverySection.infoBox.header}</InfoHeaderText>
        }
        bodyContent={<InfoContent>{deliverySection.infoBox.text}</InfoContent>}
        footerContent={
          <SecondaryContentPopperButton label={t('Close')} onClick={cancel} />
        }
      />
    </>
  );
};

const InfoButton = styled(UnstyledButton)``;

const InfoSection = styled.div`
  position: relative;
  width: 100%;
  border-radius: 4px;
  background-color: ${COLORS.Grey4};
  font-size: ${FONT_SIZES.px10};
  line-height: 1.5;
  padding: 8px 24px 8px 12px;

  ${InfoButton} {
    cursor: pointer;
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const InfoHeaderText = styled(Text)`
  ${mobileOnlyLinaria(`
    text-align: center;
  `)}
`;

const InfoContent = styled(Text)`
  line-height: 1.4;
`;

const BadgeIcon = styled(Image)`
  width: 16px;
  aspect-ratio: 1/1;
`;
